import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { X, Trash2 } from 'lucide-react';

const variants = {
  hidden: { x: '100%' },
  visible: { x: 0 },
  exit: { x: '100%' },
};

const today = () => new Date().toISOString().split('T')[0];

const FilterDrawer = ({ isOpen, onClose, fields, initialFilters, onApply }) => {
  const normalizedInitialFilters = useMemo(() => initialFilters ?? {}, [initialFilters]);

  const getInitialValues = () => {
    const values = { ...normalizedInitialFilters };
    fields.forEach((field) => {
      if (field.type === 'daterange') {
        if (values[field.id] && typeof values[field.id] === 'object') {
          values[`${field.id}_from`] = values[field.id].$gte
            ? new Date(values[field.id].$gte).toISOString().split('T')[0]
            : '';
          values[`${field.id}_to`] = values[field.id].$lte
            ? new Date(values[field.id].$lte).toISOString().split('T')[0]
            : '';
        }
      }
    });

    Object.keys(values).forEach((key) => {
      if (values[key] === '' || values[key] === undefined || values[key] === null) {
        delete values[key];
      }
    });
    return values;
  };

  const [formValues, setFormValues] = useState(getInitialValues);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormValues(getInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedInitialFilters, fields, isOpen]);

  const validate = (values) => {
    const newErrors = {};
    fields.forEach((field) => {
      if (field.type === 'daterange') {
        const from = values[`${field.id}_from`];
        const to = values[`${field.id}_to`];
        if (from && to && from > to) {
          newErrors[`${field.id}_range`] = 'La fecha inicial no puede ser mayor que la final';
        }
      }
    });
    return newErrors;
  };

  const handleInputChange = (id, value) => {
    const updated = { ...formValues, [id]: value };
    setFormValues(updated);
    setErrors(validate(updated));
  };

  const handleClear = () => {
    const cleared = {};
    fields.forEach((f) => {
      if (f.type === 'daterange') {
        cleared[`${f.id}_from`] = '';
        cleared[`${f.id}_to`] = '';
      } else {
        cleared[f.id] = '';
      }
    });
    setFormValues(cleared);
    setErrors({});
  };

  const transformFilters = (values) => {
    const transformed = {};
    fields.forEach((field) => {
      if (field.type === 'daterange') {
        const from = values[`${field.id}_from`];
        const to = values[`${field.id}_to`];
        if (from && to) {
          transformed[field.id] = {
            $gte: new Date(`${from}T00:00:00.000`),
            $lte: new Date(`${to}T23:59:59.999`),
          };
        }
      } else {
        const val = values[field.id];
        if (val !== '' && val !== undefined && val !== null) {
          transformed[field.id] = val;
        }
      }
    });
    return transformed;
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleApply = () => {
    const parsed = transformFilters(formValues);
    onApply(parsed);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed top-0 right-0 h-full w-96 bg-card shadow-2xl border-l border-border z-50 flex flex-col"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={variants}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className="flex justify-between items-center p-4 border-b border-border">
            <h2 className="text-lg font-bold text-foreground">Filtros</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {fields.map((field) => {
              if (field.type === 'daterange') {
                const errorKey = `${field.id}_range`;
                return (
                  <div key={field.id} className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-foreground">{field.label}</label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={formValues[`${field.id}_from`] || ''}
                        onChange={(e) => handleInputChange(`${field.id}_from`, e.target.value)}
                      />
                      <input
                        type="date"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={formValues[`${field.id}_to`] || ''}
                        onChange={(e) => handleInputChange(`${field.id}_to`, e.target.value)}
                      />
                    </div>
                    {errors[errorKey] && (
                      <span className="text-destructive text-xs">{errors[errorKey]}</span>
                    )}
                  </div>
                );
              }

              return (
                <div key={field.id} className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={formValues[field.id] || ''}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                    >
                      <option value="">— Todos —</option>
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || 'text'}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={formValues[field.id] || ''}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      placeholder={field.label}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-4 border-t border-border flex justify-between gap-2">
            <Button variant="outline" onClick={handleClear}>
              <Trash2 className="w-4 h-4 mr-2" /> Limpiar
            </Button>
            <Button onClick={handleApply} disabled={hasErrors}>
              Aplicar
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FilterDrawer;
